/** HeartsStore — エネルギーシステム(Royal Match / Match Factory 風)。
 *
 *  Reference: docs/PM_UPGRADE_PLAN.md §5 Hearts Energy Design
 *  - 最大 5 ハート
 *  - 1 ハート / 25 分 で自然回復
 *  - PLAY 時に 1 消費(ステージ開始のゲート)
 *  - localStorage で永続化、起動時に経過時間ぶん補充(tick)
 *
 *  公開 API: count / max / msToNext / consume() / addOne() / refillFull()
 *  各メソッドは内部で tick() を呼んで状態を最新化する。
 */

const MAX_HEARTS = 5;
const REGEN_MS = 25 * 60 * 1000;
const LS_HEARTS = 'ck.hearts';
const LS_LAST_UPDATE = 'ck.heartsLastUpdate';

export class HeartsStore {
  private hearts: number;
  private lastUpdate: number; // epoch ms

  constructor() {
    const loaded = this.load();
    this.hearts = loaded.hearts;
    this.lastUpdate = loaded.lastUpdate;
    this.tick();
  }

  /** 現在のハート数。 */
  get count(): number {
    this.tick();
    return this.hearts;
  }

  get max(): number {
    return MAX_HEARTS;
  }

  /** 次の 1 ハート補充までのミリ秒。満タンなら 0。 */
  get msToNext(): number {
    this.tick();
    if (this.hearts >= MAX_HEARTS) return 0;
    return Math.max(0, REGEN_MS - (Date.now() - this.lastUpdate));
  }

  /** 経過時間に応じてハートを補充(idempotent、頻繁に呼んでよい)。 */
  tick(): void {
    if (this.hearts >= MAX_HEARTS) {
      this.lastUpdate = Date.now();
      this.save();
      return;
    }
    const now = Date.now();
    const elapsed = now - this.lastUpdate;
    if (elapsed < 0) {
      // 端末時刻が巻き戻った等の異常 → リセット
      this.lastUpdate = now;
      this.save();
      return;
    }
    const regenCount = Math.floor(elapsed / REGEN_MS);
    if (regenCount > 0) {
      const before = this.hearts;
      this.hearts = Math.min(MAX_HEARTS, this.hearts + regenCount);
      this.lastUpdate += (this.hearts - before) * REGEN_MS;
      if (this.hearts >= MAX_HEARTS) this.lastUpdate = now;
      this.save();
    }
  }

  /** 1 ハート消費(ステージ開始時など)。残量が 0 なら false を返し、消費しない。 */
  consume(): boolean {
    this.tick();
    if (this.hearts <= 0) return false;
    const wasMax = this.hearts >= MAX_HEARTS;
    this.hearts--;
    // 満タンからの消費時、最後の更新時刻を「今」に更新して 25 分の自然回復タイマを開始
    if (wasMax) this.lastUpdate = Date.now();
    this.save();
    return true;
  }

  /** ハートを 1 加算(ad reward 等)。 */
  addOne(): void {
    this.tick();
    this.hearts = Math.min(MAX_HEARTS, this.hearts + 1);
    if (this.hearts >= MAX_HEARTS) this.lastUpdate = Date.now();
    this.save();
  }

  /** 完全補充(Color 250 等で full refill)。 */
  refillFull(): void {
    this.hearts = MAX_HEARTS;
    this.lastUpdate = Date.now();
    this.save();
  }

  /** デバッグ・進行リセット用に手動でセット。 */
  resetTo(count: number): void {
    this.hearts = Math.max(0, Math.min(MAX_HEARTS, Math.floor(count)));
    this.lastUpdate = Date.now();
    this.save();
  }

  private load(): { hearts: number; lastUpdate: number } {
    try {
      const h = parseInt(window.localStorage.getItem(LS_HEARTS) ?? '', 10);
      const t = parseInt(window.localStorage.getItem(LS_LAST_UPDATE) ?? '', 10);
      return {
        hearts:
          Number.isFinite(h) && h >= 0 ? Math.min(MAX_HEARTS, Math.floor(h)) : MAX_HEARTS,
        lastUpdate: Number.isFinite(t) ? t : Date.now(),
      };
    } catch {
      return { hearts: MAX_HEARTS, lastUpdate: Date.now() };
    }
  }

  private save(): void {
    try {
      window.localStorage.setItem(LS_HEARTS, String(this.hearts));
      window.localStorage.setItem(LS_LAST_UPDATE, String(this.lastUpdate));
    } catch {
      /* noop(プライベートモード等) */
    }
  }
}
